import React from 'react';

import { commitLocalUpdate, useRelayEnvironment } from 'react-relay';

import {
	useExtensionsActions,
	useExtensionsState,
	useHubContext,
} from '@atlassian/company-hub-common';

export const useHubThemingState = ({ cloudId, hubAri }: { cloudId: string; hubAri: string }) => {
	const hubContext = useHubContext();
	const { primary: brandingPrimaryColor } = hubContext.branding.colors;

	const [isThemingPanelOpen, setIsThemingPanelOpen] = React.useState(false);

	const [{ selectedExtensionLocalId }] = useExtensionsState();
	const { setSelectedExtensionLocalId } = useExtensionsActions();

	const openThemingPanel = React.useCallback((): void => {
		if (selectedExtensionLocalId) {
			setSelectedExtensionLocalId(null);
		}
		setIsThemingPanelOpen(true);
	}, [selectedExtensionLocalId, setSelectedExtensionLocalId]);

	const closeThemingPanel = React.useCallback((): void => {
		setIsThemingPanelOpen(false);
	}, [setIsThemingPanelOpen]);

	React.useEffect(() => {
		if (isThemingPanelOpen && selectedExtensionLocalId) {
			// If the user clicks on the extension while the panel is open, close it
			setIsThemingPanelOpen(false);
		}
	}, [isThemingPanelOpen, selectedExtensionLocalId]);

	const toggleThemingPanel = React.useCallback((): void => {
		if (isThemingPanelOpen) {
			closeThemingPanel();
		} else {
			openThemingPanel();
		}
	}, [isThemingPanelOpen, openThemingPanel, closeThemingPanel]);

	const environment = useRelayEnvironment();

	const setPrimaryColor = React.useCallback(
		(newColor: string): void => {
			commitLocalUpdate(environment, (store) => {
				const root = store.getRoot();
				const helpCenter = root.getOrCreateLinkedRecord('helpCenter', 'HelpCenter', {
					cloudId,
				});
				const helpCenterById = helpCenter.getOrCreateLinkedRecord('helpCenterById', 'HelpCenter', {
					helpCenterAri: hubAri,
				});
				const helpCenterBranding = helpCenterById.getOrCreateLinkedRecord(
					'helpCenterBranding',
					'HelpCenterBranding',
				);
				helpCenterBranding
					.getOrCreateLinkedRecord('colors', 'HelpCenterBrandingColors')
					.setValue(newColor, 'primary');
			});
		},
		[hubAri, cloudId, environment],
	);

	return {
		isThemingPanelOpen,
		primaryColor: brandingPrimaryColor,
		setPrimaryColor,
		openThemingPanel,
		closeThemingPanel,
		toggleThemingPanel,
	};
};
